// Date field for application forms. Stores and displays MM/DD/YYYY — the
// wire format CardConnect and our validators expect.
//
// Typing is the primary path: for dates a person already knows (a date of
// birth, the year a business opened) a keyboard is faster than any calendar,
// so the text input is always editable and the calendar is a secondary
// affordance behind the button.
//
// The field normalizes what was typed ("3/5/1995" -> "03/05/1995") but never
// reinterprets it. An impossible date such as 02/31/2025 keeps whatever the
// operator typed and reports an error on blur — quietly sliding it to 03/03
// would put a date nobody entered onto a boarding application.

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Calendar } from "lucide-react";
import { FieldErrorText } from "./application/FormField";
import {
  dateFieldError,
  formatDateInput,
  todayIso,
  type DateFieldRules,
} from "@/lib/formatters";
import { DatePickerCalendar, parseIsoDate } from "./DatePickerCalendar";

/** Default max width — fits MM/DD/YYYY plus the calendar button. */
const DEFAULT_MAX_WIDTH = "max-w-[11.5rem]";

/** Gap between the field and the popover, and from the viewport edge. */
const POPOVER_GAP = 4;
const VIEWPORT_MARGIN = 8;

interface Props {
  id?: string;
  /** MM/DD/YYYY value, or empty string. */
  value: string;
  onChange: (value: string) => void;
  /** Bounds and messages — the same object the page validator passes to
   *  `dateFieldError`, so the calendar can't offer a date that submit
   *  would reject. See `businessStartDateRules` / `ownerDobRules`. */
  rules?: DateFieldRules;
  /** Error from the page validator. When present the field defers to it
   *  rather than rendering a second message of its own. */
  error?: string;
  placeholder?: string;
  required?: boolean;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  ariaLabel?: string;
  /** Override width constraint (default: narrow date width). Pass "w-full" to expand. */
  className?: string;
}

/** Shifts a YYYY-MM-DD string by whole years, clamping Feb 29 to Feb 28. */
function isoShiftYears(iso: string, delta: number): string {
  const base = parseIsoDate(iso);
  if (!base) return iso;
  const year = base.getFullYear() + delta;
  const month = base.getMonth();
  const day = Math.min(base.getDate(), new Date(year, month + 1, 0).getDate());
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function panelFocusables(panel: HTMLElement): HTMLElement[] {
  return Array.from(
    panel.querySelectorAll<HTMLElement>(
      'button:not([disabled]):not([tabindex="-1"]), select:not([disabled])',
    ),
  );
}

export function DatePicker({
  id,
  value,
  onChange,
  rules = {},
  error,
  placeholder = "MM/DD/YYYY",
  required,
  ariaInvalid,
  ariaDescribedBy,
  ariaLabel,
  className,
}: Props) {
  const { min: minDate, max: maxDate, minAge, label: fieldLabel = "Date" } = rules;
  const autoId = useId();
  const inputId = id ?? `dp-${autoId}`;
  const popoverId = `${inputId}-calendar`;
  const errorId = `${inputId}-date-err`;

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(
    null,
  );
  const [blurError, setBlurError] = useState<string | null>(null);

  // An 18-year minimum age is also an upper bound on the date: don't let the
  // calendar show years it would only reject.
  const effectiveMax = useMemo(() => {
    const bounds = [maxDate];
    if (minAge != null) bounds.push(isoShiftYears(todayIso(), -minAge));
    const set = bounds.filter((b): b is string => !!b).sort();
    return set[0];
  }, [maxDate, minAge]);

  const closePopover = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    setPosition(null);
    if (restoreFocus) inputRef.current?.focus();
  }, []);

  // Position against the viewport: flip above the field when there isn't
  // room below, and pull back inside the right edge.
  useLayoutEffect(() => {
    if (!open) return;

    function place() {
      const anchor = rootRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) return;
      const a = anchor.getBoundingClientRect();
      const p = panel.getBoundingClientRect();

      const fitsBelow =
        a.bottom + POPOVER_GAP + p.height <= window.innerHeight - VIEWPORT_MARGIN;
      const fitsAbove = a.top - POPOVER_GAP - p.height >= VIEWPORT_MARGIN;
      const top =
        fitsBelow || !fitsAbove
          ? a.bottom + POPOVER_GAP
          : a.top - POPOVER_GAP - p.height;

      const maxLeft = window.innerWidth - VIEWPORT_MARGIN - p.width;
      const left = Math.max(VIEWPORT_MARGIN, Math.min(a.left, maxLeft));

      setPosition({ top, left });
    }

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      closePopover(false);
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        closePopover(true);
        return;
      }
      // The popover renders in a portal, so it isn't in the document's tab
      // order. Cycle within it and let Escape be the way out.
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel?.contains(document.activeElement)) return;
      const items = panelFocusables(panel);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, closePopover]);

  function handleChange(raw: string) {
    // Clear the blur message as soon as they start fixing it; re-nagging on
    // every keystroke of a half-typed date is noise.
    if (blurError) setBlurError(null);
    onChange(formatDateInput(raw, value));
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    // Moving to the calendar button or into the popover isn't leaving the field.
    const next = e.relatedTarget as Node | null;
    if (rootRef.current?.contains(next)) return;
    if (panelRef.current?.contains(next)) return;
    setBlurError(dateFieldError(value, rules));
  }

  function commit(mdy: string) {
    setBlurError(null);
    onChange(mdy);
    closePopover(true);
  }

  function clear() {
    setBlurError(null);
    onChange("");
    closePopover(true);
  }

  // The page validator owns the message when it has one, so the field never
  // shows two errors for the same input.
  const ownError = error ? null : blurError;
  const describedBy =
    [ariaDescribedBy, ownError ? errorId : null].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div className={className ?? DEFAULT_MAX_WIDTH}>
      <div ref={rootRef} className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          maxLength={10}
          aria-required={required || undefined}
          aria-invalid={ariaInvalid || !!ownError || undefined}
          aria-describedby={describedBy}
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={open ? popoverId : undefined}
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          className="input pr-10"
        />
        <button
          type="button"
          onClick={() => (open ? closePopover(true) : setOpen(true))}
          aria-label={open ? "Close calendar" : "Open calendar"}
          aria-expanded={open}
          aria-controls={open ? popoverId : undefined}
          tabIndex={-1}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1.5 text-brand-muted transition-colors hover:bg-brand-border/50 hover:text-brand-text focus:outline-none focus:ring-2 focus:ring-brand-blue/40"
        >
          <Calendar className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {ownError && <FieldErrorText id={errorId}>{ownError}</FieldErrorText>}

      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={popoverId}
            role="dialog"
            aria-label={`Choose ${fieldLabel.toLowerCase()}`}
            className="fixed z-[100]"
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              // Transparent, not hidden, for the pre-paint frame before it's
              // measured: `visibility: hidden` would make the grid unfocusable
              // and swallow the calendar's focus-the-selected-day on open.
              opacity: position ? 1 : 0,
            }}
          >
            <DatePickerCalendar
              selected={value}
              minDate={minDate}
              maxDate={effectiveMax}
              onSelect={commit}
              onClear={clear}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}
